import type { UIStateAPI } from "@/types";
import { zTheme } from "@/data/schemas";

interface ThemeAdapterDeps {
  readonly store: any;
  readonly uiState: UIStateAPI;
  readonly eventBus: any;
  readonly getPath: (key: string) => string | null;
  readonly persist: () => void;
}

export const hydrateThemeFromSession = (
  deps: Pick<ThemeAdapterDeps, "store" | "getPath">,
  screen: Record<string, unknown> | null | undefined
) => {
  if (!screen) return;
  const { store, getPath } = deps;
  const tPath = getPath("theme");
  if (!tPath) return;
  try {
    store.registerSchema(tPath, zTheme);
  } catch {}
  const theme = (screen as any).theme;
  if (theme === "light" || theme === "dark") {
    store.set(tPath, theme as any, { source: "hydration" });
  }
};

export const registerThemeAdapter = (deps: ThemeAdapterDeps): (() => void) => {
  const { store, uiState, eventBus, persist } = deps;
  const unsubscribes: (() => void)[] = [];

  // Debug toggles
  const readDebugEnabled = (): boolean => {
    try {
      const d =
        (globalThis as any)?.localStorage?.getItem?.("debug.adapters") ||
        (globalThis as any)?.sessionStorage?.getItem?.("debug.adapters") ||
        (globalThis as any)?.__debugAdapters ||
        "";
      if (typeof d !== "string") return !!(d as any)?.theme || d === "*";
      const parts = d.split(",").map((s: string) => s.trim());
      return parts.includes("*") || parts.includes("theme");
    } catch {
      return false;
    }
  };
  let debugEnabled = readDebugEnabled();
  const debug = (...args: unknown[]) => {
    if (debugEnabled) {
      try {
        // eslint-disable-next-line no-console
        console.debug("[adapter:theme]", ...args);
      } catch {}
    }
  };
  try {
    const unsubDbg = eventBus.on(
      "debug:adapter",
      (payload: { name?: string; enabled?: boolean } | undefined) => {
        try {
          if (!payload) return;
          if (payload.name === "theme") debugEnabled = !!payload.enabled;
          if (payload.name === "*" && typeof payload.enabled === "boolean")
            debugEnabled = !!payload.enabled;
        } catch {}
      }
    );
    unsubscribes.push(unsubDbg);
  } catch {}

  const unsubscribeStoreTheme = store.subscribe(
    (path: string) => path.endsWith(".screenState.theme"),
    ({ value, source }: { value: unknown; source?: string }) => {
      const isHydration = source === "hydration";
      try {
        if (value === "light" || value === "dark") {
          (uiState as any).setTheme(value);
          debug("store→UI theme", { value, source });
        }
      } catch {}
      if (!isHydration && source !== "socket") persist();
    },
    { debounceMs: 50 }
  );
  unsubscribes.push(unsubscribeStoreTheme);

  // UIState -> Store (theme)
  const unsubscribeUIStateChanged = eventBus.on(
    "ui-state-changed",
    (data: any) => {
      try {
        const theme = data?.state?.theme;
        const tPath = (deps.getPath as any)("theme") as string | null;
        if (!tPath) return;
        if (theme === "light" || theme === "dark") {
          try {
            store.registerSchema(tPath, zTheme);
          } catch {}
          debug("UI→store theme", { theme });
          store.set(tPath, theme as any, { source: "themeAdapter" });
        }
      } catch {}
    }
  );
  unsubscribes.push(unsubscribeUIStateChanged);

  const cleanup = () => {
    for (const u of unsubscribes) {
      try {
        u();
      } catch {}
    }
  };
  try {
    const unsub = eventBus.on("app:destroy", () => cleanup());
    unsubscribes.push(unsub);
  } catch {}
  return cleanup;
};
